import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Container, Typography, Card, CardContent, 
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, alpha, Grid
} from '@mui/material';
import { 
  Web, Storage, Speed, ArrowForward, Search, HelpOutline, 
  AutoAwesome, Code, IntegrationInstructions, Security, FlashOn, Layers, 
  ExpandMore
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Architecture & SSR",
    question: "When should I choose Next.js Server-Side Rendering (SSR) over a standard React SPA?",
    answer: "Choose Next.js (SSR/SSG/ISR) when search engine indexing (SEO), fast initial page loads (LCP), and dynamic social sharing previews are critical to user acquisition. A standard React Single Page Application (SPA) is better suited for gated SaaS dashboards or internal tools where public search indexing is unnecessary."
  },
  {
    category: "Architecture & SSR",
    question: "What is the difference between Server Components and Client Components in Next.js?",
    answer: "React Server Components (RSC) render exclusively on the server, reducing the JavaScript bundle size delivered to the browser. Client Components run on the browser to handle user interactivity, state management, and event listeners. Strategic separation minimizes bundle size while preserving interactive UX."
  },
  {
    category: "Performance & SEO",
    question: "How do you guarantee top Core Web Vitals and Lighthouse scores?",
    answer: "Through explicit performance budgeting: automatic image optimization (WebP/AVIF conversions with responsive srcsets), route-based code splitting, critical CSS extraction, server-driven dynamic rendering, structural JSON-LD schema generation, and minimizing third-party script blocking."
  },
  {
    category: "Performance & SEO",
    question: "How is structured data (JSON-LD) implemented for organic SEO authority?",
    answer: "Semantic metadata and structured JSON-LD schemas (such as Service, Organization, Product, and FAQPage) are dynamically injected into page headers during build/render time, enabling search engines to generate rich snippets and authoritative search result placements."
  },
  {
    category: "Backend & Integration",
    question: "How are frontends connected to Firebase, headless CMS, or REST/GraphQL APIs?",
    answer: "Data fetching is decoupled via centralized service abstraction layers using strongly-typed API clients (e.g., Axios, TanStack Query, or Apollo Client). Server-side cached requests or SWR patterns ensure optimistic UI updates with automatic cache invalidation."
  },
  {
    category: "Backend & Integration",
    question: "How is user authentication and session security managed?",
    answer: "Authentication leverages OAuth 2.0 / OpenID Connect using Firebase Auth or NextAuth. Sessions are secured via HTTP-only SameSite cookies or short-lived JWT tokens stored securely to prevent Cross-Site Scripting (XSS) and CSRF exploits."
  },
  {
    category: "Design & UX",
    question: "How do you ensure full cross-browser and mobile viewport responsiveness?",
    answer: "Layouts are engineered mobile-first using modern CSS Grid and Flexbox primitives inside structured UI systems like Material UI or Tailwind CSS. Breakpoint-driven fluid typography and layout recalculations guarantee consistent usability from mobile screens to 4K monitors."
  },
  {
    category: "Design & UX",
    question: "Can an existing legacy web codebase be migrated incrementally to Next.js?",
    answer: "Yes. Using incremental adoption strategies (such as reverse-proxy routing via NGINX or Vercel edge rewrites), modern React/Next.js routes can be deployed alongside legacy pages piece-by-piece without requiring a high-risk full application rebuild."
  }
];

const WebDevelopment = () => {
  const navigate = useNavigate();

  const siteUrl = "https://vikashtiwaridev.web.app/services/web-development";
  const pageTitle = "Web Development Services (React & Next.js) | Vikash Tiwari";
  const pageDescription = "Full-stack web application engineering using React, Next.js, and Firebase. Responsive, SEO-optimized, and lightning-fast web interfaces.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Web Application Engineering",
    "serviceType": "Web Development",
    "provider": {
      "@type": "Person",
      "name": "Vikash Tiwari",
      "url": "https://vikashtiwaridev.web.app/"
    },
    "areaServed": "Worldwide",
    "description": pageDescription
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Architecture & SSR', 'Performance & SEO', 'Backend & Integration', 'Design & UX'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const features = [
    { 
      title: "React & Next.js Architecture", 
      desc: "Build modern Single Page Applications (SPAs) or Server-Side Rendered (SSR/ISR) web platforms engineered for execution speed and global scale.",
      icon: <Web sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "SEO & Core Web Vitals Optimization", 
      desc: "Achieve top Lighthouse scores through automated route-level code splitting, WebP image pipelines, dynamic caching, and rich JSON-LD schemas.",
      icon: <Speed sx={{ fontSize: 30, color: '#7C4DFF' }} />
    },
    { 
      title: "RESTful API & Backend Integration", 
      desc: "Seamlessly connect decoupled frontends to Firebase, Node.js microservices, GraphQL gateways, or headless CMS endpoints.",
      icon: <Storage sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "Responsive Material UI / Tailwind", 
      desc: "Deliver accessible, pixel-perfect user interfaces designed to adapt effortlessly across mobile, tablet, desktop, and ultra-wide viewports.",
      icon: <Layers sx={{ fontSize: 30, color: '#7C4DFF' }} />
    }
  ];

  const metrics = [
    { value: "99+", label: "Lighthouse Score" },
    { value: "<0.8s", label: "Target LCP Metric" },
    { value: "100%", label: "Core Web Vitals Pass" },
    { value: "3x", label: "Conversion Lift" }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        <meta name="keywords" content="React Developer, Next.js Developer, Web Application Development, Full-Stack Engineer, Frontend Developer India" />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>
        
        {/* ================= HERO SECTION ================= */}
        <Box 
          sx={{ 
            position: 'relative', 
            pt: { xs: 10, md: 16 }, 
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0.08) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }} 
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>
              
              <Chip 
                icon={<AutoAwesome sx={{ fontSize: '18px !important', color: '#00E5FF' }} />} 
                label="Modern Web Engineering" 
                sx={{ 
                  mb: 3, 
                  py: 2, 
                  px: 1,
                  bgcolor: alpha('#00E5FF', 0.08), 
                  color: '#00E5FF', 
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }} 
              />

              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' }, 
                  fontWeight: 900, 
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                High-Performance{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Web Application Engineering
                </Box>
              </Typography>

              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#94A3B8', 
                  fontSize: { xs: '1.1rem', md: '1.35rem' }, 
                  fontWeight: 400, 
                  lineHeight: 1.6, 
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                High-converting, accessible, and ultra-fast web platforms engineered with React, Next.js, TypeScript, and modern backend integration layers.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button 
                  variant="contained" 
                  size="large" 
                  endIcon={<ArrowForward />} 
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    boxShadow: '0px 8px 24px rgba(0, 229, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #00D2EE 0%, #6B38FF 100%)',
                      boxShadow: '0px 12px 32px rgba(0, 229, 255, 0.45)',
                    }
                  }}
                >
                  Build Your Web App
                </Button>

                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#00E5FF',
                      bgcolor: alpha('#00E5FF', 0.05)
                    }
                  }}
                >
                  Technical FAQs
                </Button>
              </Stack>

              {/* Stats Bar */}
              <Grid container spacing={2}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: '16px', 
                        bgcolor: 'rgba(15, 23, 42, 0.6)', 
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 800, 
                          background: 'linear-gradient(90deg, #00E5FF, #7C4DFF)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>

        {/* ================= CORE FEATURES (Strict 2x2 Grid with Correct Padding) ================= */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' }, 
                fontWeight: 800, 
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Web Engineering Focus
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Delivering modular, scalable, and search-optimized web applications designed for conversion and long-term maintainability.
            </Typography>
          </Box>

          {/* 2x2 CSS Grid Layout with Corrected Container Padding */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              rowGap: { xs: 3, md: 10 },
              columnGap: { xs: 3, md: 4 }
            }}
          >
            {features.map((item, i) => (
              <Card 
                key={i}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  borderRadius: '20px', 
                  bgcolor: 'rgba(15, 23, 42, 0.5)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 3, // Single padding source on parent container
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 229, 255, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box 
                    sx={{ 
                      width: 56, height: 56, borderRadius: '14px', 
                      bgcolor: 'rgba(255, 255, 255, 0.03)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= TECH STACK & ECOSYSTEM ================= */}
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, 
            bgcolor: 'rgba(15, 23, 42, 0.7)', 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                Frontend & Full-Stack Toolchain
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                Production-grade technologies utilized across modern web builds.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1.5 }}>
              {[
                "React.js", "Next.js", "TypeScript", "Tailwind CSS", "Material UI", 
                "Firebase", "Node.js", "GraphQL", "TanStack Query", "Zustand", 
                "Vercel", "Lighthouse / Core Web Vitals", "Jest / Cypress", "SEO / JSON-LD"
              ].map((tech) => (
                <Chip 
                  key={tech} 
                  label={tech} 
                  sx={{ 
                    py: 2.5, px: 2, 
                    fontSize: '0.95rem', 
                    fontWeight: 600,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    color: '#E2E8F0',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#00E5FF', 0.1),
                      borderColor: '#00E5FF',
                      color: '#00E5FF'
                    }
                  }} 
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ================= TECHNICAL FAQ SECTION ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#7C4DFF' }} />} 
              label="Technical FAQs" 
              sx={{ mb: 2, bgcolor: alpha('#7C4DFF', 0.1), color: '#7C4DFF', fontWeight: 600 }} 
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Detailed answers on SSR choices, Core Web Vitals targets, backend integration, and SEO execution.
            </Typography>
          </Box>

          {/* Search Bar & Category Filter */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search Web Development FAQs (e.g., SSR, Core Web Vitals, SEO, Next.js)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00E5FF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#00E5FF' },
                  '&.Mui-focused fieldset': { borderColor: '#7C4DFF' }
                }
              }}
            />

            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#00E5FF', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': { 
                  color: '#94A3B8', 
                  fontWeight: 600, 
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#00E5FF' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#00E5FF' }} />}
                      sx={{ p: 2.5, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip 
                        label={faq.category} 
                        size="small" 
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try searching for alternative topics or select "All".</Typography>
              </Box>
            )}
          </Box>

        </Container>

        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15) 0%, rgba(124, 77, 255, 0.15) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.25)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Ready to Upgrade Your Web Application?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Schedule a technical scoping session to analyze your architecture, evaluate Next.js SSR advantages, and map out your frontend roadmap.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Get Started
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default WebDevelopment;